import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { jobCategoriesAPI } from '../services/api';
import { CategorySummary } from '../types';
import JobCard from '../components/JobCard';
import { EmptyState, SectionHeader } from '../components/ui';
import { SEO } from '../components/seo/SEO';

const CategoryChildLanding: React.FC = () => {
  const { parent: parentSlug, child: childSlug } = useParams<{ parent: string; child: string }>();
  const [summary, setSummary] = React.useState<CategorySummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    if (!parentSlug || !childSlug) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    jobCategoriesAPI
      .getSummary(parentSlug, childSlug)
      .then((data) => { if (!cancelled) setSummary(data); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [parentSlug, childSlug]);

  if (loading) return <p>Loading…</p>;
  if (notFound || !summary) return <EmptyState title="Category not found" description="Browse all open jobs on the /jobs page." />;

  const cat = summary.category;
  const parent = cat.parent!;
  const jobs = summary.jobs || [];
  const total = summary.total ?? jobs.length;
  const siblings = summary.siblings || [];
  const topSkills = summary.top_skills || [];
  const title = `${cat.name} Jobs in India`;
  const description = `${total} open ${cat.name.toLowerCase()} role${total === 1 ? '' : 's'} across India. AI-ranked by relevance from top companies on talixjobs.`;

  return (
    <div className="page-container">
      <SEO title={title} description={description} />
      <nav className="crumbs">
        <Link to="/">Home</Link> ›
        <Link to="/jobs">Jobs</Link> ›
        <Link to={`/jobs/category/${parent.slug}`}>{parent.name}</Link> ›
        <span>{cat.name}</span>
      </nav>

      <SectionHeader title={title} description={cat.description || undefined} />

      <h2>Open positions</h2>
      {jobs.length === 0 ? (
        <EmptyState title={`No ${cat.name.toLowerCase()} roles right now`} description="Check back later or browse related roles." />
      ) : (
        jobs.map((j) => <JobCard key={j.id} job={j} />)
      )}

      {total > jobs.length && (
        <p className="meta">
          Showing {jobs.length} of {total}.{' '}
          <Link to={`/jobs?category=${cat.slug}`}>See all →</Link>
        </p>
      )}

      {siblings.length > 0 && (
        <>
          <h2>Related roles</h2>
          <p>
            {siblings.map((s, i) => (
              <React.Fragment key={s.id}>
                <Link to={`/jobs/category/${parent.slug}/${s.slug}`}>{s.name}</Link>
                {i < siblings.length - 1 ? ' · ' : ''}
              </React.Fragment>
            ))}
          </p>
        </>
      )}

      {topSkills.length > 0 && (
        <>
          <h2>Top skills</h2>
          <p>
            {topSkills.map((s, i) => (
              <React.Fragment key={s.id}>
                <Link to={`/jobs/skill/${slugifyLocal(s.name)}`}>{s.name}</Link>
                {i < topSkills.length - 1 ? ' · ' : ''}
              </React.Fragment>
            ))}
          </p>
        </>
      )}
    </div>
  );
};

// Local slugify to avoid coupling to any specific util. Mirrors the backend slugify shape.
function slugifyLocal(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export default CategoryChildLanding;
